import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import posthog from 'posthog-js'
import { supabaseGestionPension as supabase } from '../../../../config/settings/supabase-client'
import routes from '../../../../config/settings/routes'
import {
  buildAccountInactiveLoginPath,
  signOutIfInactive,
} from '../services/authSupabase'
import { getLogger } from '../../../../core/debuggers'
import LoginLoading from '../../../../components/Spinners/LoginLoading'

const logger = getLogger('AuthCallback')

const CANCELLED_MESSAGE = 'Inicio de sesión cancelado. Puedes volver a intentarlo cuando quieras.'

const readOAuthError = () => {
  const search = new URLSearchParams(window.location.search)
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''))

  const error = search.get('error') || hash.get('error')
  const errorCode = search.get('error_code') || hash.get('error_code')
  const rawDescription =
    search.get('error_description') || hash.get('error_description') || ''
  const errorDescription = rawDescription.replace(/\+/g, ' ').trim()

  return { error, errorCode, errorDescription }
}

const buildLoginPath = (message) => {
  if (!message) return routes.login
  return `${routes.login}?authError=${encodeURIComponent(message)}`
}

export default function AuthCallback() {
  const navigate = useNavigate()
  const [statusMessage, setStatusMessage] = useState('Completando inicio de sesión…')

  useEffect(() => {
    let cancelled = false

    const handleCallback = async () => {
      const { error, errorDescription } = readOAuthError()

      if (error) {
        const isCancelled =
          error === 'access_denied' ||
          errorDescription.toLowerCase().includes('access_denied') ||
          errorDescription.toLowerCase().includes('denied')

        const message = isCancelled
          ? CANCELLED_MESSAGE
          : errorDescription || `No se pudo iniciar sesión (${error}).`

        logger.debug('OAuth callback error:', { error, errorDescription })
        navigate(buildLoginPath(message), { replace: true })
        return
      }

      // detectSessionInUrl may still be parsing the URL; retry briefly.
      for (let attempt = 0; attempt < 5; attempt += 1) {
        const { data, error: sessionError } = await supabase.auth.getSession()

        if (cancelled) return

        if (sessionError) {
          logger.error('Error loading session:', sessionError)
          navigate(
            buildLoginPath('No se pudo completar el inicio de sesión. Inténtalo de nuevo.'),
            { replace: true }
          )
          return
        }

        if (data.session) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('status')
            .eq('id', data.session.user.id)
            .maybeSingle()

          if (await signOutIfInactive(profile?.status)) {
            posthog.capture('account_inactive_blocked')
            logger.debug('Inactive account, redirecting to login')
            navigate(buildAccountInactiveLoginPath(), { replace: true })
            return
          }

          logger.debug('Session ready, redirecting to dashboard')
          navigate(routes.dashboard, { replace: true })
          return
        }

        setStatusMessage('Esperando confirmación de Google…')
        await new Promise((resolve) => setTimeout(resolve, 250))
      }

      if (!cancelled) {
        navigate(
          buildLoginPath('No se encontró una sesión válida. Inténtalo de nuevo.'),
          { replace: true }
        )
      }
    }

    handleCallback()

    return () => {
      cancelled = true
    }
  }, [navigate])

  return <LoginLoading message={statusMessage} />
}
